import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import './CatanBellCurve.css';

class DiceDeck {
    constructor() {
        this.resetDeck();
    }

    resetDeck() {
        this.deck = [];
        const distribution = {
            2: 1, 3: 2, 4: 3, 5: 4, 6: 5, 7: 6,
            8: 5, 9: 4, 10: 3, 11: 2, 12: 1
        };

        for (const [number, count] of Object.entries(distribution)) {
            for (let i = 0; i < count; i++) {
                this.deck.push(parseInt(number));
            }
        }

        this.shuffle();
    }

    shuffle() {
        for (let i = this.deck.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [this.deck[i], this.deck[j]] = [this.deck[j], this.deck[i]];
        }
    }

    draw() {
        if (this.deck.length === 0) {
            this.resetDeck();
        }
        return this.deck.pop();
    }

    getRemainingCount() {
        return this.deck.length;
    }
}

const CatanBellCurve = () => {
    const [currentValue, setCurrentValue] = useState('?');
    const [remainingCount, setRemainingCount] = useState(36);
    const [currentStreak, setCurrentStreak] = useState(0);
    const [lastValue, setLastValue] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const deckRef = useRef(new DiceDeck());
    const currentCardRef = useRef(null);

    const updateStreakDisplay = (value) => {
        if (value === lastValue) {
            setCurrentStreak(prev => prev + 1);
        } else {
            setCurrentStreak(1);
        }
        setLastValue(value);
    };

    const drawCard = () => {
        const value = deckRef.current.draw();
        setCurrentValue(value);
        updateStreakDisplay(value);
        setRemainingCount(deckRef.current.getRemainingCount());
        
        if (currentCardRef.current) {
            currentCardRef.current.classList.add('card-drawn');
            setTimeout(() => {
                if (currentCardRef.current) {
                    currentCardRef.current.classList.remove('card-drawn');
                }
            }, 300);
        }
    };

    const resetDeck = () => {
        deckRef.current.resetDeck();
        setCurrentValue('?');
        setCurrentStreak(0);
        setLastValue(null);
        setRemainingCount(deckRef.current.getRemainingCount());
        setShowModal(false);
    };

    return (
        <>
            <Helmet>
                {/* Override default meta tags first */}
                <meta property="og:title" content="Gaussian Catan" />
                <meta property="og:image" content="https://nickcook.vercel.app/images/catan/catan.png" />
                <meta name="description" content="A tool to reduce randomness in Catan by using a deck of cards that follows the bell curve distribution of dice rolls." />
                
                {/* Then add additional meta tags */}
                <title>Gaussian Catan | Nicholas Cook</title>
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://nickcook.vercel.app/extra/catan" />
                <meta property="og:description" content="A tool to reduce randomness in Catan by using a deck of cards that follows the bell curve distribution of dice rolls." />

                {/* Twitter */}
                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:url" content="https://nickcook.vercel.app/extra/catan" />
                <meta property="twitter:title" content="Gaussian Catan" />
                <meta property="twitter:description" content="A tool to reduce randomness in Catan by using a deck of cards that follows the bell curve distribution of dice rolls." />
                <meta property="twitter:image" content="https://nickcook.vercel.app/images/catan/catan.png" />
            </Helmet>
            <div className="catan-page">
                <div className="catan-container">
                    <h1>Gaussian Catan</h1>
                    
                    <div className="card-container">
                        <div className="current-card" ref={currentCardRef}>
                            <div className="card-value">{currentValue}</div>
                            {currentStreak > 1 && (
                                <div className="streak-display visible">
                                    {currentStreak}x streak
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="info-panel">
                        <div className="remaining-cards">
                            Cards remaining: <span>{remainingCount}</span>
                        </div>
                        <div className="button-group">
                            <button className="draw-button" onClick={drawCard}>
                                Draw Card
                            </button>
                            <button 
                                className="refresh-button" 
                                onClick={() => setShowModal(true)}
                                title="Reset Deck"
                            >
                                <svg viewBox="0 0 24 24" width="24" height="24">
                                    <path fill="currentColor" d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="distribution-info">
                        <h2>Card Distribution</h2>
                        <div className="distribution-grid">
                            <div className="distribution-item">2: 1 card</div>
                            <div className="distribution-item">3: 2 cards</div>
                            <div className="distribution-item">4: 3 cards</div>
                            <div className="distribution-item">5: 4 cards</div>
                            <div className="distribution-item">6: 5 cards</div>
                            <div className="distribution-item">7: 6 cards</div>
                            <div className="distribution-item">8: 5 cards</div>
                            <div className="distribution-item">9: 4 cards</div>
                            <div className="distribution-item">10: 3 cards</div>
                            <div className="distribution-item">11: 2 cards</div>
                            <div className="distribution-item">12: 1 card</div>
                        </div>
                    </div>

                    {showModal && (
                        <div className="modal visible" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
                            <div className="modal-content">
                                <h3>Reset Deck?</h3>
                                <p>Are you sure you want to reset the deck? This will shuffle all cards back into the deck.</p>
                                <div className="modal-buttons">
                                    <button className="confirm-button" onClick={resetDeck}>
                                        Reset Deck
                                    </button>
                                    <button className="cancel-button" onClick={() => setShowModal(false)}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default CatanBellCurve; 